// session.* (ops plan 2026-10-14-staff-agents; doc 18 "Live sessions"; lane K): the live-session service. Its first part is
// the menu review link: Radish staff open a link for one location and one preview of its draft menu, and send it to the
// merchant, whose page swaps the link's secret for a short `review_link` token (`POST /v1/auth/review-link/session`)
// that reaches only that location and that preview and can record their approval (`onboarding.menu.recordApproval`).
// The coordinator's conditions (2026-10-15): the secret travels in the URL's fragment, never its path or query; the
// token is bound to the link's preview as well as its location; revoking a link stops its tokens on their next call;
// the link's creator is the staff member, and every swap of a link for a token is audited. `session` is a sub-namespace
// of `ops`.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand } from '../../define/operations.ts';
import { secret } from '../../define/secret.ts';
import { IsoInstant, LocationId } from '../../primitives/codecs.ts';
import { MenuPreviewId, OnboardingPreviewNotFound } from '../catalog/menu-bundle.ts';
import { SupportCallNotFound } from './support.ts';

const LINKS_CLOUD = 'Review links are served from the cloud, which holds every published preview and checks each link on every call.';

/** One review link: minted by the caller (UUIDv7), so a retried open replays rather than making a second link. */
export const ReviewLinkId = z.uuid({ version: 'v7' }).meta({ id: 'ReviewLinkId' });

/** The number is not one this cloud may text (DIALPAD_ALLOWED_NUMBERS: staging texts only its test numbers). */
export const ReviewLinkNumberNotAllowed = defineError({
  code: 'session.number_not_allowed', httpStatus: 403, message: 'errors.session.number_not_allowed', retryable: false,
  details: z.object({}),
});

/** Dialpad did not take the text; no link was opened, so nothing half-done is left behind. */
export const ReviewLinkTextFailed = defineError({
  code: 'session.text_failed', httpStatus: 503, message: 'errors.session.text_failed', retryable: true,
  details: z.object({ status: z.int().nonnegative() }),
});

/** No review link has this id at this location. */
export const ReviewLinkNotFound = defineError({
  code: 'session.link_not_found', httpStatus: 404, message: 'errors.session.link_not_found', retryable: false,
  details: z.object({ linkId: ReviewLinkId }),
});

export const OpenReviewLink = defineCommand({
  name: 'session.openReviewLink',
  summary:
    "Open a review link for one location's published menu preview, to send to the merchant: it lasts seven days, reaches only that location and that preview, and its address is in the answer once.",
  input: z.object({
    linkId: ReviewLinkId, locationId: LocationId, previewId: MenuPreviewId,
    /**
     * Text the link to this number (E.164) through Dialpad as it opens: only a number this cloud may text
     * (DIALPAD_ALLOWED_NUMBERS), and nothing is opened if the text fails. Personal data, never logged.
     */
    sendTo: z.string().regex(/^\+[1-9]\d{6,14}$/).optional(),
    /**
     * The support call (support.calls) the review happens on, which must be a call matched to this location. The
     * merchant's approval through this link carries it (MenuApproved's callId). An opaque id, never logged.
     */
    callId: z.uuid().optional(),
  }),
  output: z.object({
    linkId: ReviewLinkId,
    /** Whether the link was texted (`sendTo`). */
    texted: z.boolean().optional(),
    /** The address to send (D7 secret: the first answer only; the stored result a retry replays leaves it out). */
    url: secret(z.string()).optional(),
    expiresAt: IsoInstant,
  }),
  permission: 'onboarding.menu.manage',
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LINKS_CLOUD,
  emits: [],
  errors: [OnboardingPreviewNotFound, ReviewLinkNumberNotAllowed, ReviewLinkTextFailed, SupportCallNotFound],
  // A dry run checks the preview (and the number) and stores and texts nothing, so the address it answers never opens anything.
  dryRun: true,
  agent: { expose: true, surfaces: ['staff.onboarding'], description: 'Send the url to the merchant (SMS or the call); it is shown only this once.' },
});

export const RevokeReviewLink = defineCommand({
  name: 'session.revokeReviewLink',
  summary: 'Stop a review link: the merchant page and every token already given out from it stop working at once.',
  input: z.object({ linkId: ReviewLinkId, locationId: LocationId }),
  output: z.object({ linkId: ReviewLinkId, revokedAt: IsoInstant }),
  permission: 'onboarding.menu.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LINKS_CLOUD,
  emits: [],
  errors: [ReviewLinkNotFound],
  dryRun: false,
  agent: { expose: true, surfaces: ['staff.onboarding'] },
});

export const sessionCommands = [OpenReviewLink, RevokeReviewLink] as const;
